import {EditorSDK} from '@wix/platform-editor-sdk';
import {sliderWidthMigration} from '../migration-scripts/sliderMigration';
import {reinstallPage} from '../migration-scripts/reinstallPage';
import {FedopsInteractions, wishlistPageId} from '../constants';
import {freeProductsLegacyFlag} from '../migration-scripts/freeProductsLegacyFlag';
import {productWithSubscriptionSaved} from '../migration-scripts/productWithSubscriptionSaved';
import {EcomPlatformInstallationState} from '../services/EcomPlatformInstallationState';
import {DependantApps} from '../services/DependantApps';
import {updateEcomPagesForPagesPanel} from './publicApi';
import {StoresWidgetID} from '@wix/wixstores-client-core/dist/src/constants';
import {EcomComponent} from '@wix/ecom-platform-sdk';
import * as _ from 'lodash';
import {COMPONENTS_METADATA, ComponentType} from '@wix/ecom-platform-sdk/dist/src/constants';
import {PageData} from '@wix/editor-platform-sdk-types';
import {Logger as WebBiLogger} from '@wix/web-bi-logger/dist/src/types';
import {exposureEventForTests} from '@wix/bi-logger-ec-sf/v2';
import {WIX_ECOM, WIX_NEW_STORES} from '@wix/app-definition-ids';

const STORES_WIDGETS_TO_REMOVE = [
  StoresWidgetID.ADD_TO_CART,
  StoresWidgetID.GRID_GALLERY,
  StoresWidgetID.PRODUCT_WIDGET,
  StoresWidgetID.SLIDER_GALLERY,
];

export async function handleAction(
  {type, payload},
  {
    sdk,
    dependantApps,
    ecomPlatformInstallationState,
    webBiLogger,
    fedopsLogger,
  }: {
    sdk: EditorSDK;
    dependantApps: DependantApps;
    ecomPlatformInstallationState: EcomPlatformInstallationState;
    webBiLogger: WebBiLogger;
    fedopsLogger: Record<string, any>;
  }
) {
  // eslint-disable-next-line @typescript-eslint/tslint/config
  try {
    fedopsLogger?.interactionStarted?.(FedopsInteractions.EcomHandleAction);
    if (type === 'removeAppCompleted') {
      const appToRemove = payload?.appDefinitionId;
      const isTheRemovedAppOnEcom = ecomPlatformInstallationState.checkIfAppIsOnEcom(appToRemove);
      if (isTheRemovedAppOnEcom) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        webBiLogger.report(
          exposureEventForTests({
            testName: 'appToRemoveComponents-start',
            is_eligible: true,
            appState: JSON.stringify(ecomPlatformInstallationState.getInstalledAppDefIds() ?? 'false'),
          })
        );
        const getTheRemovedAppEcomComponents = ecomPlatformInstallationState.getTheAppEcomComponents(appToRemove);
        await handleEcomDependencyAppRemoved(
          sdk,
          dependantApps,
          ecomPlatformInstallationState,
          appToRemove,
          getTheRemovedAppEcomComponents,
          webBiLogger
        );
      }
    } else if (type === 'appInstalled' && payload?.appDefinitionId === WIX_ECOM) {
      fedopsLogger?.interactionEnded?.(FedopsInteractions.EcomPlatformInstallationAppInstalled);
    } else if (type === 'migrate') {
      switch (payload.type) {
        case 'sliderWidth':
          return await sliderWidthMigration(sdk);
        case 'installNewStores':
          return await dependantApps.installNewStoresAppIfNeeded();
        case 'reinstallWishlist':
          return await reinstallPage(
            sdk,
            wishlistPageId,
            dependantApps.installWishlistPageInMembersArea.bind(dependantApps)
          );
        case 'reinstallCheckout':
          return await reinstallPage(sdk, 'checkout', dependantApps.addCheckoutIfNeeded.bind(dependantApps));
        case 'installCheckout':
          return await dependantApps.addCheckoutIfNeeded();
        case 'tryInstallWishlistTPA':
          return await dependantApps.installWishlistTPA();
        case 'freeProductsLegacyFlag':
          return await freeProductsLegacyFlag(sdk, payload.value);
        case 'productWithSubscriptionSaved':
          return await productWithSubscriptionSaved(sdk);
        default:
          return await Promise.resolve();
      }
    } else {
      return await Promise.resolve();
    }
  } catch (e) {
    fedopsLogger?.interactionFailed?.(FedopsInteractions.EcomHandleAction);
    return await Promise.reject(e);
  } finally {
    fedopsLogger?.interactionEnded?.(FedopsInteractions.EcomHandleAction);
  }
}

async function handleEcomDependencyAppRemoved(
  sdk: EditorSDK,
  dependantApps: DependantApps,
  ecomPlatformInstallationState: EcomPlatformInstallationState,
  appToRemove: string,
  appToRemoveComponents: EcomComponent[],
  webBiLogger: WebBiLogger
) {
  const allComponentsRefs: ComponentRef[] = await sdk.components.getAllComponents('');
  const allComponents: ComponentSpec[] = await sdk.components.get('', {
    componentRefs: allComponentsRefs,
    properties: ['data'],
  });
  const allPages = await sdk.pages.data.getAll('');
  const siteComponents = {
    pages: allPages,
    widgets: allComponents,
  };

  ecomPlatformInstallationState.removeApp(appToRemove);
  const allAppsRemoved = !ecomPlatformInstallationState.hasInstalledApps();
  if (allAppsRemoved) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    webBiLogger.report(
      exposureEventForTests({
        testName: 'appToRemoveComponents-start',
        is_eligible: true,
        appState: JSON.stringify(ecomPlatformInstallationState.getInstalledAppDefIds() ?? 'false'),
      })
    );
    await sdk.application.uninstall('', {openConfirmation: false});
  } else {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    webBiLogger.report(
      exposureEventForTests({
        testName: 'appToRemoveComponents-remove-pages',
        is_eligible: true,
        appState: JSON.stringify(ecomPlatformInstallationState.getInstalledAppDefIds() ?? 'false'),
      })
    );
    await removeUnneededEcomPages(sdk, siteComponents, ecomPlatformInstallationState, appToRemoveComponents);
  }

  if (ecomPlatformInstallationState.getInstalledAppDefIds().length === 1) {
    await updateEcomPagesForPagesPanel(sdk, ecomPlatformInstallationState);
  }

  if (!allAppsRemoved && appToRemove === WIX_NEW_STORES) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    webBiLogger.report(
      exposureEventForTests({
        testName: 'appToRemoveComponents-remove-stores',
        is_eligible: true,
        appState: JSON.stringify(ecomPlatformInstallationState.getInstalledAppDefIds()),
      })
    );
    await removeStoresWidgets(sdk, dependantApps, siteComponents);
  }
}

async function removeUnneededEcomPages(
  sdk: EditorSDK,
  siteComponents: {pages: PageData[]; widgets: ComponentSpec[]},
  ecomPlatformInstallationState: EcomPlatformInstallationState,
  appToRemoveComponents: EcomComponent[]
) {
  const installedComponents = _.flatten(
    ecomPlatformInstallationState
      .getInstalledAppDefIds()
      .map((app) => ecomPlatformInstallationState.getTheAppEcomComponents(app))
  );

  return Promise.all(
    appToRemoveComponents.map(async (component) => {
      if (installedComponents.includes(component)) {
        return Promise.resolve();
      }

      if (COMPONENTS_METADATA[component].type === ComponentType.PAGE) {
        const pageToRemove = siteComponents.pages.find((page) => page.tpaPageId === COMPONENTS_METADATA[component].id);
        return pageToRemove
          ? sdk.pages.remove('', {pageRef: {id: pageToRemove.id, type: 'DESKTOP'}})
          : Promise.resolve();
      } else {
        const widgetIdToRemove = COMPONENTS_METADATA[component].widget.widgetId;
        const compToRemove = siteComponents.widgets.find((comp) => comp.data?.widgetId === widgetIdToRemove);
        return compToRemove ? sdk.components.remove('', {componentRef: compToRemove.componentRef}) : Promise.resolve();
      }
    })
  );
}

async function removeStoresWidgets(
  sdk: EditorSDK,
  dependantApps: DependantApps,
  siteComponents: {pages: PageData[]; widgets: ComponentSpec[]}
) {
  await Promise.all(
    siteComponents.widgets
      .filter((comp) => STORES_WIDGETS_TO_REMOVE.includes(comp.data?.widgetId))
      .map(async (comp) => {
        await sdk.components.remove('', {componentRef: comp.componentRef});
      })
  );

  await dependantApps.uninstallWishlistPageInMembersArea(sdk);
}
